import { memo, useCallback, type MutableRefObject } from "react";
import CountdownTimer from "./CountdownTimer";

export type GameAnswerTimerClockProps = {
  isSecondChanceActive: boolean;
  isSecondChanceExpired: boolean;
  playerTimerPaused: boolean;
  secondChanceKey: number;
  questionType?: string;
  qrTimerCap?: number;
  isMainTimerStopped: boolean;
  onStartSecondChance: () => void;
  onSecondChanceTimeUp: () => void;
  onResumePlayerTimer: () => void;
  secondChanceSeconds: number;
  elapsedSecondsRef: MutableRefObject<number>;
  secondChanceSecondsRef: MutableRefObject<number>;
};

/**
 * Isolated game-answer timer UI. Ticks update refs only so the parent page does not re-render every second.
 */
const GameAnswerTimerClock = memo(function GameAnswerTimerClock({
  isSecondChanceActive,
  isSecondChanceExpired,
  playerTimerPaused,
  secondChanceKey,
  questionType,
  qrTimerCap,
  isMainTimerStopped,
  onStartSecondChance,
  onSecondChanceTimeUp,
  onResumePlayerTimer,
  secondChanceSeconds,
  elapsedSecondsRef,
  secondChanceSecondsRef,
}: GameAnswerTimerClockProps) {
  const mainTimerAutoStart = questionType !== "qr_code";
  const elapsedCap = questionType === "qr_code" ? (qrTimerCap ?? 60) : undefined;
  const elapsedRedThreshold =
    elapsedCap != null ? Math.max(elapsedCap - 5, 1) : 55;

  const handleElapsedTimeUpdate = useCallback(
    (seconds: number) => {
      elapsedSecondsRef.current = seconds;
    },
    [elapsedSecondsRef],
  );

  const handleSecondChanceTimeUpdate = useCallback(
    (seconds: number) => {
      secondChanceSecondsRef.current = seconds;
    },
    [secondChanceSecondsRef],
  );

  const noopResetClick = useCallback(() => {}, []);

  if (isSecondChanceActive) {
    return (
      <CountdownTimer
        isStopped={isSecondChanceExpired}
        autoStart={!isSecondChanceExpired}
        key={secondChanceKey}
        initialTime={secondChanceSeconds}
        initialRemainingOverride={isSecondChanceExpired ? 0 : undefined}
        onTimeUp={onSecondChanceTimeUp}
        onTimeUpdate={handleSecondChanceTimeUpdate}
        onResetClick={isSecondChanceExpired ? onResumePlayerTimer : onStartSecondChance}
        onExpiredPlay={onResumePlayerTimer}
        handleStartSecondChance={onStartSecondChance}
      />
    );
  }

  return (
    <CountdownTimer
      initialTime={elapsedSecondsRef.current}
      elapsedMode
      elapsedRedThreshold={elapsedRedThreshold}
      elapsedCap={elapsedCap}
      autoStart={mainTimerAutoStart && !playerTimerPaused}
      isStopped={isMainTimerStopped}
      onTimeUpdate={handleElapsedTimeUpdate}
      onResetClick={noopResetClick}
      handleStartSecondChance={onStartSecondChance}
    />
  );
});

export default GameAnswerTimerClock;
